import React from 'react';
import styled from 'styled-components';
import { partition } from 'lodash-es';
import { ISystemToastProps, ToastProps, ToastType } from './HBToast.types';
import { SystemToast } from './SystemToast/SystemToast';
import { Z_INDEX } from '../../styles/variables';
import MemberToastManager from './MemberToast/MemberToastManager';
import { HBButtonCustomTheme } from '../HBButton/HBButton.types';
import { hideInPrint } from '../../styles/mixins';
import { trackError } from '../common/error.utils';

export interface IHBToastProps {
    /** CSS class applied to the root element. */
    className?: string;
    /** Called with the toast `id` when a toast auto-dismisses or the user closes it. Remove the matching item from your `toasts` array. */
    removeItem: (id: string) => void;
    /** Array of toast items to render. Consumer owns this state — add and remove items to trigger and dismiss toasts. */
    toasts: ToastProps[];
    /** Required when any item in `toasts` has `type: ToastType.MemberToast`. Omitting it when member toasts are present logs a `trackError` and suppresses the MemberToast manager. */
    buttonTheme?: HBButtonCustomTheme;
    /** Distance from the right edge of the viewport. Numbers are treated as px;
     *  strings are used as-is (e.g. '50%'). Use to keep the toast within the content area. */
    right?: string | number;
}

const isSystemToast = (toast: ToastProps): toast is ISystemToastProps => {
    return toast.type !== ToastType.MemberToast;
};

const HBToast = ({
    className,
    removeItem,
    toasts,
    buttonTheme,
    right = 0
}: IHBToastProps) => {
    const [systemToasts, memberToasts] = partition(toasts, isSystemToast);

    const isMemberToastsActive = !!buttonTheme;

    if (memberToasts.length > 0 && !isMemberToastsActive) {
        trackError(
            new Error(
                '@HBToast: Unexpected - Member toasts were added but there is no button theme provided.'
            )
        );
    }

    return (
        <ToastsContainer data-testid="hb-toast--toasts-container">
            <SystemToastContainer className={className} $right={right}>
                {systemToasts.map(systemToast => (
                    <SystemToast
                        {...systemToast}
                        key={systemToast.id}
                        removeItem={removeItem}
                    />
                ))}
            </SystemToastContainer>

            {isMemberToastsActive && (
                <MemberToastManager
                    toasts={memberToasts}
                    removeItem={removeItem}
                    buttonTheme={buttonTheme}
                />
            )}
        </ToastsContainer>
    );
};

export default HBToast;

// -------------------------------- Styles -------------------------------- //
const ToastsContainer = styled.div`
    ${hideInPrint};
`;

const SystemToastContainer = styled.div<{ $right?: string | number }>`
    position: fixed;
    right: ${({ $right = 0 }) =>
        typeof $right === 'number' ? `${$right}px` : $right};
    margin-inline: 12px;
    top: 40px;
    z-index: ${Z_INDEX.TOAST};
    transition: all 0.4 ease-in-out, right 500ms cubic-bezier(0.5, 0, 0.3, 1);
`;
